// src/Pages/SessionReportDetail/Components/attendance-table-read-only.jsx
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import {
  Box,
  Card,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { colors } from 'Constants/Colors';
import useTranslation from 'Hooks/useTranslation';
import { SESSION_TZ } from 'Pages/EditCourseSessions/utils/session-datetime';
import SessionStatusPill from 'Pages/EditCourseSessions/Components/SessionStatusPill';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/** Date + time with 12-hour AM/PM. Example: "20 may 2026 · 02:32 PM". */
const formatDateTimeAmPm = iso => {
  if (!iso) return '—';
  const dt = DateTime.fromISO(iso, { zone: 'utc' }).setZone(SESSION_TZ);
  if (!dt.isValid) return '—';
  return dt.setLocale('es-MX').toFormat('dd LLL yyyy · hh:mm a');
};

const styles = {
  card: {
    borderRadius: 2,
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    border: `1px solid ${colors.GRAY_HR}`,
  },
  headerCell: { fontWeight: 700, fontSize: 12.5, color: colors.GRAY_DARK, py: 1.5 },
  cell: { fontSize: 13, color: colors.BLACK, py: 1.25 },
  muted: { color: colors.GRAY_DARK },
  emptyRow: { textAlign: 'center', color: colors.GRAY_DARK, py: 6, fontSize: 13 },
  row: { '&:hover': { bgcolor: colors.custom.table.row.hover } },
};

const dash = v => (v ? v : '—');

/**
 * Read-only paginated attendance table. Server-side pagination via `pagination`
 * prop; emits page changes via `onPageChange(nextPage)` (1-based).
 *
 * Rows expected shape: SessionAttendanceI (from sessions-api.ts) — optionally
 * with `user.fullName`, `markedBy.fullName`, `markedAt`.
 */
export default function AttendanceTableReadOnly({
  rows,
  pagination,
  onPageChange,
  onPageSizeChange,
  loading,
  hasActiveFilters,
}) {
  const { t2 } = useTranslation('SessionsReport');

  const total = pagination.totalItems;
  const isEmpty = !loading && rows.length === 0;

  return (
    <Card sx={styles.card} elevation={0}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={styles.headerCell}>{t2('detail.columns.employee')}</TableCell>
              <TableCell sx={styles.headerCell}>{t2('detail.columns.status')}</TableCell>
              <TableCell sx={styles.headerCell}>{t2('detail.columns.comment')}</TableCell>
              <TableCell sx={styles.headerCell}>{t2('detail.columns.markedBy')}</TableCell>
              <TableCell sx={styles.headerCell}>{t2('detail.columns.markedAt')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              rows.length === 0 &&
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  {Array.from({ length: 5 }).map((__, j) => (
                    <TableCell key={`sk-${i}-${j}`} sx={styles.cell}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {isEmpty && (
              <TableRow>
                <TableCell colSpan={5} sx={styles.emptyRow}>
                  {hasActiveFilters ? t2('detail.noResults') : t2('detail.noAttendees')}
                </TableCell>
              </TableRow>
            )}
            {!isEmpty &&
              rows.map(a => (
                <TableRow key={a.id} sx={styles.row}>
                  <TableCell sx={styles.cell}>{dash(a.user?.fullName)}</TableCell>
                  <TableCell sx={styles.cell}>
                    <SessionStatusPill status={a.status} variant="attendance" />
                  </TableCell>
                  <TableCell sx={styles.cell}>
                    {a.comment ? (
                      <Typography sx={{ fontSize: 13 }}>{a.comment}</Typography>
                    ) : (
                      <Box component="span" sx={styles.muted}>
                        —
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={styles.cell}>
                    {a.markedBy?.fullName ? (
                      a.markedBy.fullName
                    ) : (
                      <Box component="span" sx={styles.muted}>
                        —
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={styles.cell}>
                    {a.markedAt ? (
                      formatDateTimeAmPm(a.markedAt)
                    ) : (
                      <Box component="span" sx={styles.muted}>
                        —
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {total > 0 && (
        <TablePagination
          component="div"
          count={total}
          page={Math.max(0, pagination.page - 1)}
          onPageChange={(_, nextPage) => onPageChange(nextPage + 1)}
          rowsPerPage={pagination.pageSize}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          onRowsPerPageChange={e => onPageSizeChange?.(parseInt(e.target.value, 10))}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      )}
    </Card>
  );
}

AttendanceTableReadOnly.propTypes = {
  rows: PropTypes.array.isRequired,
  pagination: PropTypes.shape({
    page: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    totalItems: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
  }).isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func,
  loading: PropTypes.bool,
  hasActiveFilters: PropTypes.bool,
};

AttendanceTableReadOnly.defaultProps = {
  onPageSizeChange: undefined,
  loading: false,
  hasActiveFilters: false,
};
